// The one actor contract (fix #2; 02 Conventions; 03 Envelope; 05). Who did something is this
// shape everywhere: in event envelopes, in the persisted `event.actor_*` columns (via
// `actorToColumns` / `actorFromColumns`) and, in short form, on every table that records a write.
import { z } from "zod";
import { ActorKind, IdentityMode } from "./enums";
import { Uuid } from "./primitives";

const actorFields = {
  kind: ActorKind,
  membership_id: Uuid.optional(),
  seat_id: Uuid.optional(),
  /** The agent/ingest/service key used; taken from the credential, never from a body. */
  key_id: Uuid.optional(),
  run_id: Uuid.optional(),
  /** The person an agent acted for (formerly `owner_membership_id`). NULL in the slice. */
  acting_for_membership_id: Uuid.optional(),
  identity_mode: IdentityMode.optional(),
  crew_agent_id: Uuid.optional(),
  /** The autonomy/role policy revision applied. NULL in the slice. */
  policy_snapshot_id: Uuid.optional(),
  /** The company's `access_version` at decision time (fix #25). */
  auth_version: z.int().min(0).optional(),
  /** An `agency_user` actor's Better Auth user id (operator writes; S1-04 review #14). */
  user_id: Uuid.optional(),
};

/**
 * Only `system` and `agency_user` act without a membership (`event_actor_membership_check`).
 * `agency_user` carries its user id and the `access_version` of its decision and never a
 * membership; no other kind carries a user id (`event_actor_user_check`).
 */
function membershipRule(
  a: {
    kind: string;
    membership_id?: string | undefined;
    user_id?: string | undefined;
    auth_version?: number | undefined;
  },
  ctx: z.RefinementCtx,
) {
  const agency = a.kind === "agency_user";
  if (a.kind !== "system" && !agency && a.membership_id === undefined)
    ctx.addIssue({
      code: "custom",
      path: ["membership_id"],
      message: `${a.kind} actor requires membership_id`,
    });
  if (agency && (a.user_id === undefined || a.auth_version === undefined))
    ctx.addIssue({
      code: "custom",
      path: ["user_id"],
      message: "agency_user actor requires user_id and auth_version",
    });
  if (agency && a.membership_id !== undefined)
    ctx.addIssue({
      code: "custom",
      path: ["membership_id"],
      message: "agency_user actor has no membership",
    });
  if (!agency && a.user_id !== undefined)
    ctx.addIssue({ code: "custom", path: ["user_id"], message: `${a.kind} carries no user_id` });
}

export const Actor = z.strictObject(actorFields).superRefine(membershipRule).meta({ id: "Actor" });
export type Actor = z.infer<typeof Actor>;

// Write schemas per channel (finding #8). `Actor` above stays broad so stored events of any
// later milestone still round-trip; these say exactly which fields a slice-1 write may set.
// Run, delegation, identity-mode, crew and policy fields are M3/M4 and refused here.

/**
 * Actor of an ingest write (13 §Ingest contract rule 1, fix #29): always the key's own DE, seat
 * and key, with the authorization version. A `digital_employee` actor without `key_id` is refused.
 */
export const IngestActor = z
  .strictObject({
    kind: z.literal("digital_employee"),
    membership_id: Uuid,
    seat_id: Uuid,
    key_id: Uuid,
    auth_version: z.int().min(0),
  })
  .meta({ id: "IngestActor" });

/** Actor of a write made through a person's session: membership and `access_version` (fix #25). */
export const SessionActor = z
  .strictObject({
    kind: z.literal("person"),
    membership_id: Uuid,
    seat_id: Uuid.optional(),
    auth_version: z.int().min(0),
  })
  .meta({ id: "SessionActor" });

/** Actor of a scheduler job, consumer or migration: no membership, seat, key or run. */
export const SystemActor = z
  .strictObject({ kind: z.literal("system") })
  .meta({ id: "SystemActor" });

/**
 * Actor of an operator write: an agency user acting under a live operator grant (operator login,
 * or the `agency_operator` tuple), with the `access_version` of the decision (S1-04 review #14).
 * Agency users have no membership in the company.
 */
export const OperatorActor = z
  .strictObject({
    kind: z.literal("agency_user"),
    user_id: Uuid,
    auth_version: z.int().min(0),
  })
  .meta({ id: "OperatorActor" });

export type WriteChannel = "ingest" | "session" | "operator" | "system";
const actorByChannel = {
  ingest: IngestActor,
  session: SessionActor,
  operator: OperatorActor,
  system: SystemActor,
} as const;

/** Validates an actor for the channel that performs the write; throws `ZodError` otherwise. */
export function actorForWrite(channel: WriteChannel, actor: unknown): Actor {
  return Actor.parse(actorByChannel[channel].parse(actor));
}

// Persisted form ------------------------------------------------------------------------------

/** The `actor_*` column set of `event` (02, 13 §event). Nullable exactly as the columns are. */
export const ActorColumns = z.object({
  actor_kind: ActorKind,
  actor_membership_id: Uuid.nullable(),
  actor_seat_id: Uuid.nullable(),
  actor_key_id: Uuid.nullable(),
  actor_run_id: Uuid.nullable(),
  actor_acting_for_membership_id: Uuid.nullable(),
  actor_identity_mode: IdentityMode.nullable(),
  actor_crew_agent_id: Uuid.nullable(),
  actor_policy_snapshot_id: Uuid.nullable(),
  actor_auth_version: z.int().min(0).nullable(),
  actor_user_id: Uuid.nullable(),
});
export type ActorColumns = z.infer<typeof ActorColumns>;

/** The short actor form on every other table that records a write (13 §Conventions). */
export const ActorShortColumns = z.object({
  actor_kind: ActorKind.nullable(),
  actor_membership_id: Uuid.nullable(),
});

const pairs = [
  ["membership_id", "actor_membership_id"],
  ["seat_id", "actor_seat_id"],
  ["key_id", "actor_key_id"],
  ["run_id", "actor_run_id"],
  ["acting_for_membership_id", "actor_acting_for_membership_id"],
  ["identity_mode", "actor_identity_mode"],
  ["crew_agent_id", "actor_crew_agent_id"],
  ["policy_snapshot_id", "actor_policy_snapshot_id"],
  ["auth_version", "actor_auth_version"],
  ["user_id", "actor_user_id"],
] as const satisfies readonly (readonly [keyof Actor, keyof ActorColumns])[];

/** Actor → `event.actor_*` columns (absent fields become NULL). */
export function actorToColumns(actor: Actor): ActorColumns {
  const a = Actor.parse(actor);
  const cols: Record<string, unknown> = { actor_kind: a.kind };
  for (const [field, column] of pairs) cols[column] = a[field] ?? null;
  return ActorColumns.parse(cols);
}

/** `event.actor_*` columns → Actor (NULL columns become absent fields). */
export function actorFromColumns(columns: ActorColumns): Actor {
  const c = ActorColumns.parse(columns);
  const actor: Record<string, unknown> = { kind: c.actor_kind };
  for (const [field, column] of pairs) if (c[column] !== null) actor[field] = c[column];
  return Actor.parse(actor);
}
